import { useEffect, useRef, useCallback } from 'react'
import { isSameEntityId } from '../utils/entityId'

export interface UseAutoSelectSingleOptionArgs {
  /** Normalized id string when exactly one option exists; otherwise null */
  soleIdStr: string | null
  currentValue: unknown
  /** Called to apply the sole option (should invoke parent onChange with correct shape) */
  onApplySole: () => void
  isLoading?: boolean
  /** e.g. fetch error — skip auto-select */
  skip?: boolean
  disabled?: boolean
  autoSelectSingle?: boolean
  /** When this changes, user may auto-select again after a manual clear (branch filter, etc.) */
  resetKey?: unknown
}

/**
 * When a dropdown's option list resolves to exactly one item and value is empty,
 * applies that option via `onApplySole`. Respects manual clear via `markUserCleared`.
 */
export function useAutoSelectSingleOption({
  soleIdStr,
  currentValue,
  onApplySole,
  isLoading = false,
  skip = false,
  disabled = false,
  autoSelectSingle = true,
  resetKey,
}: UseAutoSelectSingleOptionArgs) {
  const userClearedRef = useRef(false)
  const onApplyRef = useRef(onApplySole)
  onApplyRef.current = onApplySole

  useEffect(() => {
    if (!soleIdStr) userClearedRef.current = false
  }, [soleIdStr])

  useEffect(() => {
    userClearedRef.current = false
  }, [resetKey])

  useEffect(() => {
    if (
      !autoSelectSingle ||
      disabled ||
      isLoading ||
      skip ||
      !soleIdStr ||
      userClearedRef.current
    ) {
      return
    }

    const valStr =
      currentValue === undefined || currentValue === null || currentValue === ''
        ? ''
        : String(currentValue).trim()

    if (valStr !== '' && isSameEntityId(soleIdStr, valStr)) return
    if (valStr !== '') return

    onApplyRef.current()
  }, [
    autoSelectSingle,
    disabled,
    isLoading,
    skip,
    soleIdStr,
    currentValue,
  ])

  const markUserCleared = useCallback(() => {
    userClearedRef.current = true
  }, [])

  return { markUserCleared }
}

/** Single selectable id when `items` has exactly one row with a non-empty id */
export function soleOptionIdFromList<T>(
  items: readonly T[],
  getId: (item: T) => string | number | null | undefined
): string | null {
  if (!items || items.length !== 1) return null
  const id = getId(items[0] as T)
  if (id == null || String(id).trim() === '') return null
  return String(id).trim()
}
